import { DOCUMENT, isPlatformBrowser } from '@angular/common';
import { DestroyRef, Inject, Injectable, PLATFORM_ID, inject } from '@angular/core';
import { Meta, Title } from '@angular/platform-browser';
import { TranslateService } from '@ngx-translate/core';
import { normalizeLang, langToRoute, langToOgLocale, getAlternateOgLocales } from '../localization/i18n.config';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Injectable({
  providedIn: 'root',
})
export class SeoService {
  private destroyRef = inject(DestroyRef);

  constructor(
    private translate: TranslateService,
    private title: Title,
    private meta: Meta,
    @Inject(DOCUMENT) private readonly document: Document,
    @Inject(PLATFORM_ID) private readonly platformId: Object
  ) {}

  init(): void {
    if (!isPlatformBrowser(this.platformId)) return;

    this.translate.onLangChange.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      this.setHtmlLang(normalizeLang(this.translate.currentLang));
      this.updateSeoTags();
    });

    this.setHtmlLang(normalizeLang(this.translate.currentLang));
    this.updateSeoTags();
  }

  private setHtmlLang(lang: string): void {
    this.document.documentElement.setAttribute('lang', lang);
  }

  private updateSeoTags(): void {
    const language = normalizeLang(this.translate.currentLang);
    const langSegment = langToRoute(language);
    const ogLocale = langToOgLocale(language);
    const ogLocaleAlt = getAlternateOgLocales(language)[0];
    const baseUrl = 'https://vnribeiro.dev/';
    const siteUrl = `${baseUrl}${langSegment}`;
    const imageUrl = `${baseUrl}images/vinicius-ribeiro-hero.webp`;

    this.translate
      .get([
        'meta.title',
        'meta.description',
        'meta.ogTitle',
        'meta.ogDescription',
        'meta.twitterTitle',
        'meta.twitterDescription',
        'meta.imageAlt',
      ])
      .subscribe((metaText: Record<string, string>) => {
        this.title.setTitle(metaText['meta.title']);

        this.meta.updateTag({
          name: 'description',
          content: metaText['meta.description'],
        });

        this.meta.updateTag({ property: 'og:title', content: metaText['meta.ogTitle'] });
        this.meta.updateTag({
          property: 'og:description',
          content: metaText['meta.ogDescription'],
        });
        this.meta.updateTag({ property: 'og:url', content: siteUrl });
        this.meta.updateTag({ property: 'og:image', content: imageUrl });
        this.meta.updateTag({ property: 'og:image:alt', content: metaText['meta.imageAlt'] });
        this.meta.updateTag({ property: 'og:locale', content: ogLocale });
        this.meta.updateTag({ property: 'og:locale:alternate', content: ogLocaleAlt });

        this.meta.updateTag({
          name: 'twitter:title',
          content: metaText['meta.twitterTitle'],
        });
        this.meta.updateTag({
          name: 'twitter:description',
          content: metaText['meta.twitterDescription'],
        });
        this.meta.updateTag({ name: 'twitter:image', content: imageUrl });
        this.meta.updateTag({
          name: 'twitter:image:alt',
          content: metaText['meta.imageAlt'],
        });
      });

    const canonical = this.document.querySelector<HTMLLinkElement>('link[rel="canonical"]');
    if (canonical) {
      canonical.setAttribute('href', siteUrl);
    }
  }
}


